import { useState, useCallback } from 'react';
import { RECORD_DATA_TYPE } from '@demandproai/shared';
import useFinalizeLifecareUpload from './useFinalizeLifecareUpload';
import useCaseRecordDelete from './useCaseRecordDelete';
import { prepFileDataForDedicatedUpload } from '../utils/helper';
import { toaster } from '../utils/toast';

const FIELD_KEY = 'lifeCarePlanSupportingDocuments';

const useLifeCarePlanSupportingDocuments = ({ caseId, supportingDocuments = [], onAdd, onRemove }) => {
    const [removingFileId, setRemovingFileId] = useState(null);
    const { isLoading: isUploading, dedicatedFileUpload } = useFinalizeLifecareUpload({ caseId });
    const { removeFileRecords } = useCaseRecordDelete();

    const handleFileSelection = useCallback(async ({ file: files }) => {
        if (!files?.length) return;
        const prepFiles = prepFileDataForDedicatedUpload(files);
        const uploadedFiles = await dedicatedFileUpload({
            files: prepFiles,
            caseRecordType: RECORD_DATA_TYPE.LIFE_CARE_PLAN_SUPPORTING_DOCUMENT,
            updateCasePayload: { fieldKey: FIELD_KEY, action: 'add' },
        });
        onAdd?.(uploadedFiles);
    }, [dedicatedFileUpload, onAdd]);

    const handleRemoveFile = useCallback(async (file) => {
        const fileKey = file?.s3UrlPath || file?.uniqueKey;
        if (!fileKey) return;

        setRemovingFileId(fileKey);
        try {
            await removeFileRecords({
                filesToDelete: [file],
                caseRecordType: RECORD_DATA_TYPE.LIFE_CARE_PLAN_SUPPORTING_DOCUMENT,
                data: { [FIELD_KEY]: { s3UrlPath: fileKey } },
            });
            onRemove?.(fileKey);
            toaster({ message: 'File removed successfully', success: true });
        } catch {
            toaster({ message: 'Failed to remove file', success: false });
        } finally {
            setRemovingFileId(null);
        }
    }, [removeFileRecords, onRemove]);

    return {
        files: supportingDocuments,
        isUploading,
        removingFileId,
        handleFileSelection,
        handleRemoveFile,
    };
};

export default useLifeCarePlanSupportingDocuments;
